// src/config/apiConfig.js - Global API Configuration

/**
 * API Configuration
 * 
 * To change the API URL, set the REACT_APP_API_URL environment variable
 * or modify the default values below.
 * 
 * Environment Variables (in .env file):
 * - REACT_APP_API_URL: Full API base URL (e.g., "http://localhost:8016")
 * - REACT_APP_API_HOST: API host (e.g., "localhost")
 * - REACT_APP_API_PORT: API port (e.g., "8016")
 * - REACT_APP_API_PROTOCOL: Protocol (e.g., "http" or "https")
 */

// Default API configuration
const DEFAULT_API_HOST = 'api.jaybharat.com';
const DEFAULT_API_PORT = '';
const DEFAULT_API_PROTOCOL = 'https';
const DEV_LOCAL_API_URL = 'http://localhost:8016';

/**
 * Use only scheme + host (+ port). Strips any path accidentally pasted into REACT_APP_API_URL
 * (e.g. .../api/v1, .../api) so auth + feastflow paths are never doubled.
 */
export function normalizeApiBaseUrl(value) {
  if (!value || typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  try {
    const withProto = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const u = new URL(withProto);
    return u.origin;
  } catch {
    let v = trimmed.replace(/\/+$/, '');
    v = v.replace(/\/api\/v1$/i, '');
    v = v.replace(/\/api$/i, '');
    return v;
  }
}

// Build API URL from environment variables or use defaults
const getApiUrl = () => {
  let url;
  // Priority 1: Full URL from environment variable
  if (process.env.REACT_APP_API_URL) {
    url = process.env.REACT_APP_API_URL;
  } else {
    // Priority 2: Build from individual components
    const protocol = process.env.REACT_APP_API_PROTOCOL || DEFAULT_API_PROTOCOL;
    const host = process.env.REACT_APP_API_HOST || DEFAULT_API_HOST;
    const port = process.env.REACT_APP_API_PORT || DEFAULT_API_PORT;
    url = `${protocol}://${host}${port ? `:${port}` : ''}`;
  }
  return normalizeApiBaseUrl(url);
};

// Export API configuration
export const API_CONFIG = {
  // Base URL for API calls
  BASE_URL: getApiUrl(),

  // Individual components (for proxy configuration)
  HOST: process.env.REACT_APP_API_HOST || DEFAULT_API_HOST,
  PORT: process.env.REACT_APP_API_PORT || DEFAULT_API_PORT,
  PROTOCOL: process.env.REACT_APP_API_PROTOCOL || DEFAULT_API_PROTOCOL,

  // Full URL (same as BASE_URL, but explicit)
  FULL_URL: getApiUrl(),

  // Proxy configuration
  USE_PROXY: process.env.REACT_APP_USE_PROXY !== 'false', // Default to true
  PROXY_PATH: '/api', // Proxy path prefix

  // Timeout settings
  TIMEOUT: parseInt(process.env.REACT_APP_API_TIMEOUT || '15000', 10), // 15 seconds default

  // Retry settings
  MAX_RETRIES: parseInt(process.env.REACT_APP_API_MAX_RETRIES || '2', 10),
  RETRY_DELAY: parseInt(process.env.REACT_APP_API_RETRY_DELAY || '1000', 10), // 1 second
};

// Log configuration in development
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 API Configuration:', {
    baseUrl: API_CONFIG.BASE_URL,
    host: API_CONFIG.HOST,
    port: API_CONFIG.PORT,
    protocol: API_CONFIG.PROTOCOL,
    useProxy: API_CONFIG.USE_PROXY,
    timeout: API_CONFIG.TIMEOUT,
    maxRetries: API_CONFIG.MAX_RETRIES,
    envVars: {
      REACT_APP_API_URL: process.env.REACT_APP_API_URL || '(not set)',
      REACT_APP_AUTH_API_PREFIX: process.env.REACT_APP_AUTH_API_PREFIX || '(default /api/v1)',
      REACT_APP_API_HOST: process.env.REACT_APP_API_HOST || '(not set)',
      REACT_APP_API_PORT: process.env.REACT_APP_API_PORT || '(not set)',
      REACT_APP_API_PROTOCOL: process.env.REACT_APP_API_PROTOCOL || '(not set)',
      REACT_APP_USE_PROXY: process.env.REACT_APP_USE_PROXY || '(not set)',
    }
  });
}

// Helper function to get API URL (for backward compatibility)
export const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return normalizeApiBaseUrl(process.env.REACT_APP_API_URL);
  }

  if (process.env.NODE_ENV === 'development') {
    // Opt-in CRA proxy only when explicitly enabled (relative /api/... → setupProxy.js)
    if (process.env.REACT_APP_USE_PROXY === 'true') {
      return '';
    }
    return DEV_LOCAL_API_URL;
  }

  return normalizeApiBaseUrl(API_CONFIG.BASE_URL);
};

/**
 * JayBharatAPI origin for auth session binding and feature modules.
 */
export function getEffectiveBackendOrigin() {
  return getApiBaseUrl() || DEV_LOCAL_API_URL;
}

export default API_CONFIG;

